/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ActivatedRouteSnapshot } from '@angular/router';
import { RouteParamTracker, SlicedDataQuery, SortDirection } from '@gh/core-data';
import { distinctUntilChanged$, map$, skip$, startWith$ } from '@gh/rx/operators';
import { isEqual, isNil, isNumber, omitBy } from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { GridSortModel } from './grid-state.interfaces';

/**
 * {@link GridCursor} allows to manage current position in grid (like focused cell, selected or top row,
 * state of pagination). {@link GridCursor} allows to listen for table position changes ({@link GridCursor#changes})
 * and/or update table position ({@link GridCursor#setState}).
 */
export interface GridCursor<S> {
  /**
   * Notifies about changes in table position
   */
  readonly changes: Observable<S>;

  /**
   * Updates table position
   */
  setState(state: S): void;
}

/**
 * Table position tracked by {@link GridPaginationCursor}
 */
export type GridPaginationCursorState = {
  page: number;
  pageSize: number;
  sort?: GridSortModel;
  scroll?: number;
  focusedId?: any;
};

/**
 * Implementation of {@link GridCursor} which tracks only information relevant for grid with pagination.
 */
export class GridPaginationCursor implements GridCursor<GridPaginationCursorState> {
  private changes$$: BehaviorSubject<GridPaginationCursorState>;

  constructor(state: GridPaginationCursorState) {
    this.changes$$ = new BehaviorSubject<GridPaginationCursorState>(state);
  }

  get changes(): Observable<GridPaginationCursorState> {
    return this.changes$$.asObservable().pipe(distinctUntilChanged$());
  }

  get state(): GridPaginationCursorState {
    return this.changes$$.value;
  }

  setState(state: GridPaginationCursorState): void {
    this.changes$$.next(state);
  }
}

const serializeSortDirection = (direction: SortDirection) => direction === SortDirection.Ascending ? void 0 : 'desc';
const deserializeSortDirection = (direction: string) =>
  direction === 'desc' ? SortDirection.Descending : SortDirection.Ascending;

export const newGridPaginationCursor = (state: GridPaginationCursorState = { page: 0, pageSize: -1 }) =>
  new GridPaginationCursor(state);

export const getSlicedDataQueryFromCursorTracker = ({ queryParams }: ActivatedRouteSnapshot): SlicedDataQuery => {
  const { sortField } = queryParams;
  const page = Number(queryParams.page) || 0;
  const pageSize = Number(queryParams.pageSize) || -1;
  const sortDirection = queryParams.sortDirection ? deserializeSortDirection(queryParams.sortDirection) : void 0;
  return {
    from: page * pageSize,
    to: (page + 1) * pageSize,
    sorting: sortField ? {
      field: sortField,
      direction: <SortDirection>sortDirection,
    } : void 0,
  };
};

export interface GridCursorTrackerOptions {
  defaultPageSize?: number;
  firstVisibleIndex?: number;
}

/**
 * Defines {@link RouteParamTracker} for provided {@link GridCursor}.
 */
export function cursorTracker(cursor: GridCursor<GridPaginationCursorState>,
                              options?: GridCursorTrackerOptions): RouteParamTracker<GridPaginationCursorState>;
export function cursorTracker(cursor: GridCursor<any>, options: any): RouteParamTracker<any> {
  if (cursor instanceof GridPaginationCursor) {
    let defaultState = cursor.state;
    const defaultPageSize = options && options.defaultPageSize || -1;
    const firstVisibleIndex = isNumber(options && options.firstVisibleIndex) ? options.firstVisibleIndex : -1;
    let nextPageWasRequestedAtLeastOnce = false;
    const getNextPageSize = (pageSize: number) => pageSize < 0 ? defaultPageSize : pageSize;
    const getNextPage = (pageSize: number, page?: number): number => {
      if (!nextPageWasRequestedAtLeastOnce) {
        // if we request next page the first time => guarantee that row by firstVisibleIndex is visible, if any
        nextPageWasRequestedAtLeastOnce = true;
        if (firstVisibleIndex >= 0) {
          if (pageSize > 0) {
            return Math.floor(firstVisibleIndex / pageSize);
          } else {
            console.error('cursorTracker: page size is unknown: set default page size for cursorTracker');
            return 0;
          }
        }
      }
      return page ? Number(page) : 0;
    };

    const tracker: RouteParamTracker<GridPaginationCursorState> = {
      filterChanges: cursor.changes,
      paramChanges: cursor.changes.pipe(
        // there is no sense to store initial filter in query parameters
        skip$(1),
        map$((state) =>
          isEqual(omitBy(state, isNil), omitBy(defaultState, isNil))
            // send empty set of query parameters if state is equal to default state
            ? {
              page: void 0,
              pageSize: void 0,
              sortField: void 0,
              sortDirection: void 0,
              scroll: void 0,
              focusedId: void 0,
            }
            // serialize cursor
            : ({
              page: state.page,
              pageSize: state.pageSize,
              sortField: state.sort && state.sort.field,
              sortDirection: state.sort && serializeSortDirection(state.sort.direction),
              scroll: state.scroll,
              focusedId: state.focusedId,
            })),
        startWith$({})),
      mergeParams: ({ page, pageSize, sortField, sortDirection, scroll, focusedId }) => {
        const nextPageSize = getNextPageSize(pageSize ? Number(pageSize) : -1);
        const nextPage = getNextPage(nextPageSize, page);

        cursor.setState({
          page: nextPage,
          pageSize: nextPageSize,
          sort: sortField ? { field: sortField, direction: deserializeSortDirection(sortDirection) } : void 0,
          scroll: scroll ? Number(scroll) : 0,
          focusedId,
        });
      },
      withDefault: (state: GridPaginationCursorState) => {
        defaultState = state;
        return tracker;
      },
    };
    return tracker;
  } else {
    throw new Error(`cursorTracker: GridCursor of type '${cursor.constructor.name}' is not supported`);
  }
}
